import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { customerGroupsQueryKey } from "@/components/shared/customerGroupsQueryKey";
import { ClientCustomerGroupImportItem, ClientCustomerGroupImportResult, customerGroupsApi } from "@/lib/api";
import { planClientGroupImport } from "@/lib/csvImportBatches";
import { CsvBatchImportControls } from "./CsvBatchImportControls";
import { RejectedRowsTable } from "./RejectedRowsTable";
import { SearchableColumn, SearchableTable } from "./SearchableTable";
import { MAX_REJECTED_IN_SUMMARY, downloadRejected, sortByLine, splitIntoSummaryParts } from "./csvImport";
import { useCsvBatchImport } from "./useCsvBatchImport";

function emptyResult(dryRun: boolean): ClientCustomerGroupImportResult {
  return {
    dryRun,
    counts: { totalLines: 0, linked: 0, moved: 0, unchanged: 0, rejected: 0 },
    linked: [],
    moved: [],
    unchanged: [],
    rejected: [],
  };
}

function mergeResults(total: ClientCustomerGroupImportResult, batch: ClientCustomerGroupImportResult): ClientCustomerGroupImportResult {
  return {
    dryRun: total.dryRun,
    counts: {
      totalLines: total.counts.totalLines + batch.counts.totalLines,
      linked: total.counts.linked + batch.counts.linked,
      moved: total.counts.moved + batch.counts.moved,
      unchanged: total.counts.unchanged + batch.counts.unchanged,
      rejected: total.counts.rejected + batch.counts.rejected,
    },
    linked: total.linked.concat(batch.linked),
    moved: total.moved.concat(batch.moved),
    unchanged: total.unchanged.concat(batch.unchanged),
    rejected: total.rejected.concat(batch.rejected),
  };
}

/** Os lotes agrupam por CNPJ, então as linhas chegam fora de ordem: ordena pelo número da linha no fim. */
function finishResult(result: ClientCustomerGroupImportResult): ClientCustomerGroupImportResult {
  return {
    ...result,
    linked: sortByLine(result.linked),
    moved: sortByLine(result.moved),
    unchanged: sortByLine(result.unchanged),
    rejected: sortByLine(result.rejected),
  };
}

function formatCnpj(digits: string): string {
  return digits.length === 14
    ? digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : digits;
}

const clientColumns: SearchableColumn<ClientCustomerGroupImportItem>[] = [
  { id: "line", header: "Linha", cell: (item) => item.line, searchValue: (item) => item.line },
  {
    id: "client",
    header: "Cliente",
    cell: (item) => (
      <>
        <div>{item.clientCompanyName}</div>
        <div className="text-xs text-muted-foreground">{item.clientCodeCnx}</div>
      </>
    ),
    searchValue: (item) => `${item.clientCompanyName} ${item.clientCodeCnx}`,
  },
  {
    id: "cnpj",
    header: "CNPJ",
    cell: (item) => formatCnpj(item.cnpj),
    searchValue: (item) => formatCnpj(item.cnpj),
    className: "font-mono whitespace-nowrap",
  },
  {
    id: "group",
    header: "Grupo",
    cell: (item) => item.groupCode,
    searchValue: (item) => `${item.groupCode} ${item.magentoGroupId}`,
    className: "font-mono",
  },
];

const clientColumnsWithPrevious: SearchableColumn<ClientCustomerGroupImportItem>[] = [
  ...clientColumns,
  {
    id: "previous",
    header: "Grupo anterior",
    cell: (item) => item.previousGroupCode ?? "—",
    searchValue: (item) => item.previousGroupCode,
    className: "font-mono text-sm text-muted-foreground",
  },
];

function ClientItemsTable({ items, showPrevious = false }: { items: ClientCustomerGroupImportItem[]; showPrevious?: boolean }) {
  return (
    <SearchableTable
      rows={items}
      columns={showPrevious ? clientColumnsWithPrevious : clientColumns}
      rowKey={(item) => `${item.line}-${item.clientId}`}
      itemLabel="clientes"
      emptyTitle="Nenhuma linha"
      emptyMessage="Nenhum cliente nesta situação."
    />
  );
}

function ClientImportResultView({ result, fileName }: { result: ClientCustomerGroupImportResult; fileName: string }) {
  const { counts } = result;
  const summary = [
    { label: result.dryRun ? "Serão ligados" : "Ligados", value: counts.linked, className: "text-emerald-600" },
    { label: result.dryRun ? "Vão trocar de grupo" : "Trocaram de grupo", value: counts.moved, className: "text-blue-600" },
    { label: "Inalterados", value: counts.unchanged, className: "" },
    { label: "Rejeitados", value: counts.rejected, className: counts.rejected > 0 ? "text-destructive" : "" },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        {summary.map((item) => (
          <Card key={item.label}>
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">{item.label}</p>
              <p className={`mt-1 text-2xl font-bold ${item.className}`}>{item.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{counts.totalLines} linha(s) no arquivo.</p>
        {result.rejected.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => downloadRejected(result.rejected, fileName)}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Baixar rejeitadas (CSV)
          </Button>
        )}
      </div>

      <Tabs defaultValue={counts.rejected > 0 ? "rejected" : counts.moved > 0 ? "moved" : "linked"}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="linked">Ligados ({counts.linked})</TabsTrigger>
          <TabsTrigger value="moved">Trocam de grupo ({counts.moved})</TabsTrigger>
          <TabsTrigger value="unchanged">Inalterados ({counts.unchanged})</TabsTrigger>
          <TabsTrigger value="rejected">Rejeitados ({counts.rejected})</TabsTrigger>
        </TabsList>
        <TabsContent value="linked"><ClientItemsTable items={result.linked} /></TabsContent>
        <TabsContent value="moved"><ClientItemsTable items={result.moved} showPrevious /></TabsContent>
        <TabsContent value="unchanged"><ClientItemsTable items={result.unchanged} /></TabsContent>
        <TabsContent value="rejected"><RejectedRowsTable rows={result.rejected} /></TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Importação do CSV de clientes exportado do Magento com o grupo de cada um (até 10 MB, em lotes; ver
 * useCsvBatchImport). Liga o cliente pelo CNPJ ao grupo pelo Customer Group ID (ID do Magento); o grupo do arquivo
 * sobrescreve o do portal. Os grupos precisam ter sido importados antes, do mesmo arquivo.
 */
export function ClientGroupImportCard() {
  const queryClient = useQueryClient();
  const state = useCsvBatchImport({
    plan: (content) => planClientGroupImport(content),
    emptyResult,
    merge: mergeResults,
    sendBatch: (batch, dryRun, signal) => customerGroupsApi.importClientsBatch(batch, dryRun, signal),
    finish: finishResult,
    // Uma parte de cada vez. Se uma falhar, a nova tentativa reenvia todas: as já gravadas a API ignora.
    complete: async (importId, file, result, signal) => {
      const parts = splitIntoSummaryParts(result.rejected.slice(0, MAX_REJECTED_IN_SUMMARY));
      for (const [index, rejected] of parts.entries()) {
        await customerGroupsApi.completeClientsImport(
          importId,
          { ...file, counts: result.counts, rejected, part: index + 1, totalParts: parts.length },
          signal,
        );
      }
    },
    onSaved: async (result) => {
      toast.success("Importação concluída.", {
        description: `${result.counts.linked} ligado(s), ${result.counts.moved} trocaram de grupo, ${result.counts.rejected} rejeitado(s).`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: customerGroupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: ["clients"] }),
      ]);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" aria-hidden="true" />
          Importar grupos por cliente
        </CardTitle>
        <CardDescription>
          CSV de clientes exportado do Magento, com as colunas <span className="font-mono">CNPJ</span> e{" "}
          <span className="font-mono">Customer Group ID</span> (as demais são ignoradas). O cliente é achado pelo CNPJ e
          o grupo pelo ID do Magento; o grupo do arquivo substitui o grupo atual do cliente. Importe os grupos antes,
          com o mesmo arquivo. Até 10 MB, enviado em lotes: se a importação parar no meio, retome ou envie o arquivo de
          novo, sem duplicar nada.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CsvBatchImportControls
          state={state}
          selectLabel="Selecionar arquivo CSV de clientes"
          describePlan={(plan) => `${plan.totalLines} linha(s) em ${plan.batches.length} lote(s).`}
          hasChanges={(result) => result.counts.linked + result.counts.moved > 0}
          nothingToSaveMessage="Nada a gravar: nenhum cliente do arquivo muda de grupo."
          renderResult={(result, fileName) => <ClientImportResultView result={result} fileName={fileName} />}
        />
      </CardContent>
    </Card>
  );
}
